import { memo, useEffect, useState } from "react"
import {
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  Divider,
  ListSubheader,
  FormControl,
  FormHelperText,
  Tooltip as MuiTooltip,
} from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CheckIcon from "@mui/icons-material/Check"
import Tooltip from "components/UI/Tooltip"
import { getSectionColor } from "common/sectionColors"
import { styles } from "./SelectInput.styles"

const buildMenuItems = (
  options,
  renderOption,
  stylesRef,
  selectedValue,
  variant
) => {
  if (variant === "sections") {
    const sectionsMap = new Map()
    const ungrouped = []

    options?.forEach((option) => {
      const sectionName = option?.programSectionName
      const sectionId = option?.programSectionId
      const sectionDescription = option?.programSectionDescription

      if (sectionName || sectionId != null) {
        const key = sectionId != null ? String(sectionId) : sectionName
        if (!sectionsMap.has(key)) {
          sectionsMap.set(key, {
            id: sectionId,
            name: sectionName || "",
            description: sectionDescription || "",
            options: [],
          })
        }
        const currentSection = sectionsMap.get(key)
        if (!currentSection.description && sectionDescription) {
          currentSection.description = sectionDescription
        }
        currentSection.options.push(option)
      } else {
        ungrouped.push(option)
      }
    })

    const items = []
    let sectionIndex = 0

    sectionsMap.forEach((section) => {
      const sectionColor = getSectionColor(sectionIndex)
      const isFirstSection = sectionIndex === 0
      sectionIndex++

      if (!isFirstSection) {
        items.push(
          <Divider
            key={`divider-${section.id}`}
            sx={stylesRef.sectionDivider}
          />
        )
      }

      items.push(
        <ListSubheader
          key={`section-${section.id}`}
          disableSticky
          sx={stylesRef.sectionHeaderContainer}
        >
          <Box sx={stylesRef.sectionHeaderBanner(sectionColor.bg)}>
            <Box sx={stylesRef.sectionHeaderTitleRow}>
              <Typography
                variant="subtitle2"
                sx={stylesRef.sectionHeaderTitle(sectionColor.titleColor)}
              >
                {section.name}
              </Typography>
              {Boolean(section.description) && (
                <MuiTooltip
                  title={
                    <Box sx={stylesRef.tooltipScrollBox}>
                      <Typography
                        variant="subtitle2"
                        sx={stylesRef.tooltipTitle}
                      >
                        {section.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={stylesRef.tooltipBody}
                        dangerouslySetInnerHTML={{
                          __html: section.description,
                        }}
                      />
                    </Box>
                  }
                  arrow
                  placement="right"
                  disableInteractive={false}
                  componentsProps={{
                    tooltip: { sx: stylesRef.tooltipPopper },
                    arrow: { sx: stylesRef.tooltipArrow },
                  }}
                >
                  <Box
                    component="span"
                    sx={stylesRef.infoIconWrapper}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={stylesRef.infoIcon(sectionColor.iconColor)}
                    />
                  </Box>
                </MuiTooltip>
              )}
            </Box>
          </Box>
        </ListSubheader>
      )

      section.options.forEach((option) => {
        const optValue = option.value
        const optLabel = option.label
        const itemDescription = option.contentTypeDescription
        const isSelected = selectedValue === optValue

        items.push(
          <MenuItem
            key={optValue}
            value={optValue}
            sx={stylesRef.sectionItem(sectionColor?.bg)}
          >
            {renderOption ? (
              renderOption(optValue, options, false, isSelected)
            ) : (
              <Box sx={stylesRef.sectionItemLeft}>
                <Typography
                  variant="body2"
                  sx={[
                    stylesRef.sectionItemText,
                    isSelected && { fontWeight: 600 },
                  ]}
                >
                  {optLabel}
                </Typography>
                {Boolean(itemDescription) && (
                  <MuiTooltip
                    title={
                      <Box sx={stylesRef.tooltipScrollBox}>
                        <Typography
                          variant="body2"
                          sx={stylesRef.tooltipBody}
                          dangerouslySetInnerHTML={{
                            __html: itemDescription,
                          }}
                        />
                      </Box>
                    }
                    arrow
                    placement="right"
                    disableInteractive={false}
                    componentsProps={{
                      tooltip: { sx: stylesRef.tooltipPopper },
                      arrow: { sx: stylesRef.tooltipArrow },
                    }}
                  >
                    <Box
                      component="span"
                      sx={stylesRef.infoIconWrapper}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                    >
                      <InfoOutlinedIcon sx={stylesRef.infoIcon()} />
                    </Box>
                  </MuiTooltip>
                )}
              </Box>
            )}
            {isSelected && !renderOption && (
              <CheckIcon sx={stylesRef.selectedCheckIcon} />
            )}
          </MenuItem>
        )
      })
    })

    ungrouped.forEach((option) => {
      const optValue = option.value
      const optLabel = option.label
      const itemDescription = option.contentTypeDescription
      const isSelected = selectedValue === optValue

      items.push(
        <MenuItem key={optValue} value={optValue} sx={stylesRef.sectionItem()}>
          {renderOption ? (
            renderOption(optValue, options, false, isSelected)
          ) : (
            <Box sx={stylesRef.sectionItemLeft}>
              <Typography
                variant="body2"
                sx={[
                  stylesRef.sectionItemText,
                  isSelected && { fontWeight: 600 },
                ]}
              >
                {optLabel}
              </Typography>
              {Boolean(itemDescription) && (
                <MuiTooltip
                  title={
                    <Box sx={stylesRef.tooltipScrollBox}>
                      <Typography
                        variant="body2"
                        sx={stylesRef.tooltipBody}
                        dangerouslySetInnerHTML={{
                          __html: itemDescription,
                        }}
                      />
                    </Box>
                  }
                  arrow
                  placement="right"
                  disableInteractive={false}
                  componentsProps={{
                    tooltip: { sx: stylesRef.tooltipPopper },
                    arrow: { sx: stylesRef.tooltipArrow },
                  }}
                >
                  <Box
                    component="span"
                    sx={stylesRef.infoIconWrapper}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                    }}
                  >
                    <InfoOutlinedIcon sx={stylesRef.infoIcon()} />
                  </Box>
                </MuiTooltip>
              )}
            </Box>
          )}
          {isSelected && !renderOption && (
            <CheckIcon sx={stylesRef.selectedCheckIcon} />
          )}
        </MenuItem>
      )
    })

    return items
  }

  const hasGroups = options?.some(
    (o) => typeof o === "object" && o !== null && o.group != null
  )

  if (!hasGroups) {
    return options.map((option) => {
      const optValue =
        typeof option === "string" ? option : option.id ?? option.value
      const optLabel =
        typeof option === "string" ? option : option.label ?? option.name
      return (
        <MenuItem key={optValue} value={optValue}>
          {renderOption ? (
            renderOption(optValue, options)
          ) : (
            <Typography variant="body2" sx={stylesRef.menuItemText}>
              {optLabel}
            </Typography>
          )}
        </MenuItem>
      )
    })
  }

  // Grouped rendering with ListSubheader — preserves insertion order
  const seenGroups = new Set()
  const items = []
  options.forEach((option) => {
    const optValue =
      typeof option === "string" ? option : option.id ?? option.value
    const optLabel =
      typeof option === "string" ? option : option.label ?? option.name
    const groupName = option.group ?? ""

    if (groupName && !seenGroups.has(groupName)) {
      seenGroups.add(groupName)
      items.push(
        <ListSubheader key={`group-${groupName}`} sx={stylesRef.groupHeader}>
          {groupName}
        </ListSubheader>
      )
    }

    items.push(
      <MenuItem key={optValue} value={optValue}>
        {renderOption ? (
          renderOption(optValue, options)
        ) : (
          <Typography variant="body2" sx={stylesRef.menuItemText}>
            {optLabel}
          </Typography>
        )}
      </MenuItem>
    )
  })
  return items
}

export const SelectInput = memo(
  ({
    value,
    options = [],
    placeholder,
    label,
    startAdornment,
    renderOption,
    onChange,
    disabled = false,
    isLoading = false,
    autoSelectSingle = false,
    multiple = false,
    variant,
    sx = {},
    fullWidth = false,
    error,
    helperText,
    MenuProps,
    ...props
  }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)

    useEffect(() => {
      setIsOverflowing(false)
    }, [value])

    const handleMouseEnter = (e) => {
      const target = e.currentTarget
      if (target) {
        const hasOverflow = target.scrollWidth > target.clientWidth
        if (hasOverflow !== isOverflowing) {
          setIsOverflowing(hasOverflow)
        }
      }
    }

    useEffect(() => {
      if (
        autoSelectSingle &&
        !value &&
        options.length === 1 &&
        typeof onChange === "function"
      ) {
        const singleOption = options[0]
        const singleVal =
          typeof singleOption === "string"
            ? singleOption
            : singleOption.id ?? singleOption.value
        onChange(singleVal)
      }
    }, [autoSelectSingle, value, options, onChange])

    const select = (
      <Select
        value={multiple ? (Array.isArray(value) ? value : []) : value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        multiple={multiple}
        displayEmpty
        size="small"
        fullWidth={fullWidth}
        disabled={disabled || isLoading}
        IconComponent={options?.length > 1 ? undefined : () => null}
        startAdornment={startAdornment}
        endAdornment={
          <Box
            sx={[
              styles.loadingIndicator,
              !isLoading && styles.loadingIndicatorHidden,
            ]}
          >
            <CircularProgress size={16} color="inherit" />
          </Box>
        }
        MenuProps={{
          ...styles.menuProps,
          ...(variant === "sections"
            ? {
                PaperProps: {
                  sx: styles.sectionsMenuPaper,
                },
              }
            : {}),
          ...MenuProps,
        }}
        sx={[
          styles.select,
          label && styles.selectWithLabel,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        renderValue={(selected) => {
          const isEmpty = Array.isArray(selected)
            ? selected.length === 0
            : !selected
          if (isEmpty && placeholder) {
            const placeholderEl = (
              <Typography variant="body2" sx={styles.placeholder}>
                {placeholder}
              </Typography>
            )
            return label ? (
              <Box sx={styles.labelValueStack}>
                <Typography variant="caption" sx={styles.selectLabel}>
                  {label}
                </Typography>
                {placeholderEl}
              </Box>
            ) : (
              placeholderEl
            )
          }
          if (renderOption) {
            if (Array.isArray(selected)) {
              return (
                <Box
                  sx={{
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                  }}
                >
                  {selected.map((val, index) => (
                    <Box
                      key={val}
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {renderOption(val, options, true)}
                      {index < selected.length - 1 && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ mr: 0.5 }}
                        >
                          ,
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )
            }
            return renderOption(selected, options, true)
          }
          if (Array.isArray(selected)) {
            const labels = selected.map((val) => {
              const opt = options.find(
                (o) =>
                  String(typeof o === "string" ? o : o.id ?? o.value) ===
                  String(val)
              )
              return opt
                ? typeof opt === "string"
                  ? opt
                  : opt.label ?? opt.name
                : val
            })
            return (
              <Typography variant="body2" sx={styles.valueText}>
                {labels.join(", ")}
              </Typography>
            )
          }
          const option = options.find(
            (o) =>
              String(typeof o === "string" ? o : o.id ?? o.value) ===
              String(selected)
          )
          const valueLabel = option
            ? typeof option === "string"
              ? option
              : option.label ?? option.name
            : selected
          const selectValue = (
            <Tooltip
              key={`${valueLabel}_${isOverflowing}`}
              title={typeof valueLabel === "string" ? valueLabel : ""}
              arrow
              enterDelay={400}
              leaveDelay={0}
              open={isOpen || !isOverflowing ? false : undefined}
              divStyle={styles.tooltipDiv}
            >
              <Typography
                variant="body2"
                sx={styles.valueText}
                onMouseEnter={handleMouseEnter}
              >
                {valueLabel}
              </Typography>
            </Tooltip>
          )
          return label ? (
            <Box sx={styles.labelValueStack}>
              <Typography variant="caption" sx={styles.selectLabel}>
                {label}
              </Typography>
              {selectValue}
            </Box>
          ) : (
            selectValue
          )
        }}
        {...props}
      >
        {buildMenuItems(options, renderOption, styles, value, variant)}
      </Select>
    )

    const hasFeedback = error !== undefined || helperText !== undefined
    if (!hasFeedback) return select

    return (
      <FormControl fullWidth={fullWidth} error={Boolean(error)}>
        {select}
        <FormHelperText sx={styles.helperText}>
          {helperText || "\u00A0"}
        </FormHelperText>
      </FormControl>
    )
  }
)

export default SelectInput
